import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronDown, CheckCircle, DollarSign, Award } from 'lucide-react';
import { Vendor, Category, CategoryContent, fetchVendors, organizeVendorsByType } from '../../../types/vendor.ts';
import StoryCard from './StoryCard.tsx';
import { useNavigate } from 'react-router-dom';

interface VendorCategoryNavigatorProps {
  onSelect?: (category: string) => void;
}

const VendorCategoryNavigator: React.FC<VendorCategoryNavigatorProps> = ({ onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Vendors');
  const [showAllVendors, setShowAllVendors] = useState(false);
  const [vendorData, setVendorData] = useState<Record<string, Vendor[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(100); // Increased initial display count
  const [selectedFilters, setSelectedFilters] = useState({
    verified: false,
    topVendor: false,
    priceRange: null as null | '$' | '$$' | '$$$'
  });
  const navigate = useNavigate();
  
  // Fetch vendors on component mount
  useEffect(() => {
    const getVendors = async () => {
      setIsLoading(true);
      const vendors = await fetchVendors();
      console.log('Fetched vendors:', {
        total: vendors.length,
        worldByPixels: vendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
      });
      const organizedVendors = organizeVendorsByType(vendors);
      console.log('Organized vendors:', {
        categories: Object.keys(organizedVendors),
        worldByPixels: Object.values(organizedVendors).flat().find(v => v.name.toLowerCase().includes('worldbypixels'))
      });
      setVendorData(organizedVendors);
      setIsLoading(false);
    };
    
    getVendors();
  }, []);
  
  const categories: Category[] = [
    { name: 'Vendors', color: '#FFB8D2' },    // Medium pink
    { name: 'Venues', color: '#C4C6FF' },     // Medium indigo
    { name: 'Photography', color: '#FFD4E5' }, // Soft pink
    { name: 'Decor', color: '#D8D9FF' },      // Soft indigo
    { name: 'Florists', color: '#FFC4D6' },   // Rose pink
    { name: 'Entertainment', color: '#B6B8FF' }, // Periwinkle
    { name: 'Catering', color: '#FFACC4' },   // Coral pink
    { name: 'Transport', color: '#A4A6FF' },  // Deep periwinkle
    { name: 'Planners', color: '#FFE0EB' },     // Light pink
    { name: 'Unique', color: '#FFE0EB' },     // Light pink
  ];

  // Get category vendors
  const getCategoryVendors = (category: string) => {
    if (isLoading) return [];
    
    console.log('Getting vendors for category:', {
      category,
      availableCategories: Object.keys(vendorData),
      vendorCounts: Object.entries(vendorData).reduce((acc, [cat, vendors]) => {
        acc[cat] = vendors.length;
        return acc;
      }, {} as Record<string, number>)
    });
    
    if (category === 'Vendors') {
      // For "Vendors" category, show all vendors from all categories
      const allVendors = Object.values(vendorData).flat();
      console.log('All vendors for Vendors category:', {
        total: allVendors.length,
        vendors: allVendors.map(v => ({
          name: v.name,
          type: v.vendorType,
          category: Object.entries(vendorData).find(([_, vendors]) => 
            vendors.some(vendor => vendor.id === v.id)
          )?.[0]
        }))
      });
      return allVendors;
    }
    
    // For other categories, find the category in the API data
    const normalizedRequestedCategory = category.toLowerCase().trim();
    const categoryKey = Object.keys(vendorData).find(
      key => key.toLowerCase().trim() === normalizedRequestedCategory
    );
    
    console.log('Category matching result:', {
      requestedCategory: category,
      normalizedRequestedCategory,
      matchedCategory: categoryKey,
      vendorsInCategory: categoryKey ? vendorData[categoryKey].length : 0,
      vendorNames: categoryKey ? vendorData[categoryKey].map(v => v.name) : []
    });
    
    return categoryKey ? vendorData[categoryKey] : [];
  };

  // Filter vendors based on selected filters
  const getFilteredVendors = (vendors: Vendor[]) => {
    console.log('Starting getFilteredVendors with:', {
      totalVendors: vendors.length,
      vendorNames: vendors.map(v => v.name),
      selectedFilters,
      worldByPixels: vendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
    });

    // If no filters are selected, return all vendors
    if (!selectedFilters.verified && !selectedFilters.topVendor && !selectedFilters.priceRange) {
      console.log('No filters selected, returning all vendors');
      return vendors;
    }

    // Filter vendors based on selected criteria
    const filteredVendors = vendors.filter(vendor => {
      // Verified filter - only apply if explicitly selected
      if (selectedFilters.verified && !vendor.verified) {
        console.log(`Filtering out ${vendor.name} - not verified`);
        return false;
      }
      
      // Top Vendor filter - only apply if explicitly selected
      if (selectedFilters.topVendor && !vendor.topVendor) {
        console.log(`Filtering out ${vendor.name} - not top vendor`);
        return false;
      }
      
      // Price Range filter - only apply if explicitly selected
      if (selectedFilters.priceRange) {
        const price = vendor.approximateCharge || 0;
        switch(selectedFilters.priceRange) {
          case '$':
            if (price > 2000) {
              console.log(`Filtering out ${vendor.name} - price too high for $`);
              return false;
            }
            break;
          case '$$':
            if (price < 2000 || price > 5000) {
              console.log(`Filtering out ${vendor.name} - price outside $$ range`);
              return false;
            }
            break;
          case '$$$':
            if (price < 5000) {
              console.log(`Filtering out ${vendor.name} - price too low for $$$`);
              return false;
            }
            break;
        }
      }
      
      return true;
    });

    console.log('Filtered results:', {
      totalVendors: filteredVendors.length,
      vendorNames: filteredVendors.map(v => v.name),
      worldByPixels: filteredVendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
    });

    return filteredVendors;
  };

  // Get all vendors for a category (for grid view)
  const getAllCategoryVendors = (category: string) => {
    if (isLoading) return [];
    
    console.log('Getting all vendors for category:', {
      category,
      availableCategories: Object.keys(vendorData),
      vendorCounts: Object.entries(vendorData).reduce((acc, [cat, vendors]) => {
        acc[cat] = vendors.length;
        return acc;
      }, {} as Record<string, number>)
    });
    
    let vendors: Vendor[] = [];
    if (category === 'Vendors') {
      // For "Vendors" category, return all vendors
      vendors = Object.values(vendorData).flat();
    } else {
      // For other categories, find the category in the API data
      const normalizedRequestedCategory = category.toLowerCase().trim();
      const categoryKey = Object.keys(vendorData).find(
        key => key.toLowerCase().trim() === normalizedRequestedCategory
      );
      
      console.log('Category matching for grid view:', {
        requestedCategory: category,
        normalizedRequestedCategory,
        matchedCategory: categoryKey,
        vendorsInCategory: categoryKey ? vendorData[categoryKey].length : 0,
        vendorNames: categoryKey ? vendorData[categoryKey].map(v => v.name) : []
      });
      
      vendors = categoryKey ? vendorData[categoryKey] : [];
    }

    // Log the vendors before filtering
    console.log('Vendors before filtering:', {
      category,
      totalVendors: vendors.length,
      vendorNames: vendors.map(v => v.name),
      worldByPixels: vendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
    });

    const filteredVendors = getFilteredVendors(vendors);
    
    console.log('Filtered vendors result:', {
      category,
      totalVendors: vendors.length,
      filteredCount: filteredVendors.length,
      vendorNames: filteredVendors.map(v => v.name),
      worldByPixels: filteredVendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
    });

    return filteredVendors;
  };

  // Get content for each category
  const getCategoryContent = (category: string): CategoryContent => {
    switch(category) {
      case 'Vendors':
        return {
          title: "Wedding Vendors",
          description: "Find and book the best wedding professionals to bring your vision to life.",
          stats: [
            { label: "Top Vendors", value: `${Object.values(vendorData).flat().filter((v: Vendor) => v.topVendor).length}+` },
            { label: "Categories", value: `${Object.keys(vendorData).length}` }
          ],
          actions: ["Browse All", "Top Rated"]
        };
      case 'Venues':
        return {
          title: "Wedding Venues",
          description: "Discover magical spaces that create the perfect backdrop for your special day.",
          stats: [
            { label: "Unique Venues", value: `${vendorData['Venues']?.length || 0}+` },
            { label: "Styles", value: "15+" }
          ],
          actions: ["Indoor Venues", "Outdoor Venues"]
        };
      // ... rest of the category content cases ...
      default:
        return {
          title: "Wedding Categories",
          description: "Explore our wedding categories to find everything you need for your special day.",
          stats: [
            { label: "Categories", value: `${Object.keys(vendorData).length}` },
            { label: "Vendors", value: `${Object.values(vendorData).flat().length}+` }
          ],
          actions: ["Browse All", "Get Started"]
        };
    }
  };

  // Get content for the current category
  const content = getCategoryContent(selectedCategory);
  
  // Get vendors for the current category (limited to featured)
  const vendors = getCategoryVendors(selectedCategory);
  
  // Get all vendors for the current category (for grid view)
  const allVendors = getAllCategoryVendors(selectedCategory);
  
  // Get the color for the current category
  const categoryColor = categories.find(c => c.name === selectedCategory)?.color || '#6A11CB';

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    if (!isExpanded) {
      setIsExpanded(true);
    }
    if (onSelect) {
      onSelect(category);
    }
  };

  // Handle vendor selection and navigation
  const handleVendorSelect = (vendor: Vendor) => {
    navigate(`/connect/vendor/${vendor.id}/${encodeURIComponent(vendor.name)}`);
  };

  return (
    <div className="w-full h-screen overflow-hidden flex flex-col">
      <div className="flex flex-grow overflow-hidden rounded-xl shadow-lg border border-pink-100/30 relative">
        {/* Gradient Background - Moved outside scrollable area */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Main diagonal gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-100/90 via-pink-50/80 to-purple-50/70" />
          
          {/* Ethereal light effects - Reduced blur and animation intensity */}
          <div className="absolute -top-1/4 -right-1/4 w-[120%] h-[120%] bg-gradient-to-bl from-[#FFB8D2]/20 via-transparent to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-1/4 -left-1/4 w-[120%] h-[120%] bg-gradient-to-tr from-[#C4C6FF]/20 via-transparent to-transparent rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-gradient-to-br from-white/30 via-transparent to-transparent rounded-full blur-[120px]" />
        </div>

        {/* Left: Colorful Categories Sidebar */}
        <div className="flex-shrink-0 relative backdrop-blur-sm" style={{ width: isExpanded ? '30%' : '15%' }}>
          <div className="flex h-full" style={{ minHeight: '700px' }}>
            {categories.map((category, index) => (
              <div
                key={category.name}
                style={{ 
                  backgroundColor: `${category.color}cc`,
                  width: isExpanded ? `${100 / categories.length}%` : index === 0 ? '100%' : '8%',
                  transition: 'all 0.3s ease-in-out'
                }}
                className={`h-full flex items-center justify-center relative cursor-pointer backdrop-blur-sm hover:backdrop-blur-md transition-all ${
                  category.name === selectedCategory ? 'border border-white/80 shadow-md' : ''
                }`}
                onClick={() => handleCategorySelect(category.name)}
              >
                <span 
                  className={`text-gray-700 text-lg md:text-xl font-medium tracking-wider absolute transform -rotate-90 whitespace-nowrap transition-all duration-300 ${
                    isExpanded || index === 0 ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {category.name}
                </span>
                
                {category.name === selectedCategory && (
                  <motion.div
                    layoutId="categoryIndicator"
                    className="absolute bottom-3 w-16 h-1.5 bg-white/90 rounded-full shadow-md"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Expand/Collapse Button */}
          <div 
            className="absolute z-[100]" 
            style={{ 
              right: '-20px', 
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="backdrop-blur-xl bg-white/50 hover:bg-white/60 rounded-full p-3 md:p-4 shadow-md border border-white/70 transition-all animate-bounce-right"
            >
              <ChevronRight
                className={`w-5 h-5 md:w-6 md:h-6 text-gray-700 transform transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Right: Main Content */}
        <div className="flex-grow overflow-y-auto relative">
          {/* Sticky Header and Filters */}
          <div className="sticky top-0 z-10">
            <div className="bg-white/40 backdrop-blur-sm pb-6">
              <div className="p-8 pb-0">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-4xl font-semibold text-gray-700">
                      {selectedCategory}
                    </h2>
                  </div>
                </div>

                {/* Filter Bar */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-4 py-2 bg-pink-100/40 backdrop-blur-sm rounded-lg">
                    <span className="text-pink-500 text-sm font-medium">Filters:</span>
                  </div>
                  <button 
                    onClick={() => setSelectedFilters(prev => ({ ...prev, verified: !prev.verified }))}
                    className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 backdrop-blur-sm ${
                      selectedFilters.verified
                        ? 'bg-pink-200/60 text-gray-700 shadow-md'
                        : 'bg-white/50 text-gray-600 border border-pink-100/40 hover:border-pink-200/60'
                    } text-sm font-medium`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Verified
                  </button>
                  <div className="relative">
                    <select
                      value={selectedFilters.priceRange || ''}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, priceRange: e.target.value as typeof selectedFilters.priceRange }))}
                      className={`appearance-none px-4 py-2 pr-10 rounded-lg transition-all backdrop-blur-sm ${
                        selectedFilters.priceRange
                          ? 'bg-amber-100/60 text-gray-700 shadow-md'
                          : 'bg-white/50 text-gray-600 border border-amber-100/40 hover:border-amber-200/60'
                      } text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-100/40`}
                    >
                      <option value="">All Prices</option>
                      <option value="$">$ (Budget Friendly)</option>
                      <option value="$$">$$ (Mid-Range)</option>
                      <option value="$$$">$$$ (Luxury)</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedFilters(prev => ({ ...prev, topVendor: !prev.topVendor }))}
                    className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 backdrop-blur-sm ${
                      selectedFilters.topVendor
                        ? 'bg-purple-200/60 text-gray-700 shadow-md'
                        : 'bg-white/50 text-gray-600 border border-purple-100/40 hover:border-purple-200/60'
                    } text-sm font-medium`}
                  >
                    <Award className="w-4 h-4" />
                    Top Vendor
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="p-8 pt-0">
            {/* Vendor Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 pb-4">
              {allVendors.slice(0, displayCount).map((vendor, idx) => (
                <div 
                  key={`${vendor.id}-${idx}`}
                  className="transform-gpu transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-lg hover:shadow-pink-100/30"
                  style={{
                    transformOrigin: 'center center',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <StoryCard
                    vendor={vendor}
                    index={idx}
                    onSelect={() => handleVendorSelect(vendor)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.02); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.02); }
        }
        @keyframes float-slow-reverse {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(10px) scale(1.02); }
        }
        @keyframes bounce-right {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(3px); }
        }
        .animate-bounce-right {
          animation: bounce-right 1s ease-in-out 3s infinite;
        }
      `}</style>
    </div>
  );
};

export default VendorCategoryNavigator; 